import { useState, useEffect } from 'react';

// ── Global price singleton ────────────────────────────────────────
// Fetched once on app start, refreshed every 60s.
// Any component can read it instantly via getBCHPrice().
//
// RESILIENCE: the BCH price feeds USD↔sats conversions across the app
// (tips, boosts, exchange). If it drops to null, features that price things
// in USD silently break. So we:
//   1. Try several price sources, each via the CORS proxy, and stop at the
//      first that succeeds — a single API rate-limiting us (CoinGecko's free
//      endpoint returns 429s) no longer kills the price.
//   2. Persist the last-known price to localStorage and seed from it on load,
//      so a transient outage keeps a usable (if slightly stale) price instead
//      of null.

const PRICE_CACHE_KEY = 'bchnostr:bch-price';
const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Seed from the last known good price so we're never null on a cold start. */
function loadPersistedPrice(): { price: number | null; change24h: number } {
  try {
    const raw = localStorage.getItem(PRICE_CACHE_KEY);
    if (raw) {
      const { price, change24h } = JSON.parse(raw);
      if (typeof price === 'number' && isFinite(price) && price > 0) {
        return { price, change24h: typeof change24h === 'number' ? change24h : 0 };
      }
    }
  } catch {}
  return { price: null, change24h: 0 };
}

function persistPrice(price: number, change24h: number): void {
  try {
    localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify({ price, change24h, at: Date.now() }));
  } catch {}
}

const _seed = loadPersistedPrice();
let _cachedPrice: number | null = _seed.price;
let _cachedChange24h: number = _seed.change24h;
let _priceFetchPromise: Promise<void> | null = null;
let _priceInterval: ReturnType<typeof setInterval> | null = null;

function proxied(url: string): string {
  return CORS_PROXY + encodeURIComponent(url);
}

// Each source returns { price, change24h } or null. Tried in order until one
// works. Routed through the CORS proxy for reliability across browsers.
const PRICE_SOURCES: Array<() => Promise<{ price: number; change24h: number } | null>> = [
  // 1) CoinGecko (has 24h change)
  async () => {
    const res = await fetch(proxied('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin-cash&vs_currencies=usd&include_24hr_change=true'), { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const data = await res.json();
    const p = data?.['bitcoin-cash']?.usd;
    if (typeof p !== 'number' || p <= 0) return null;
    return { price: p, change24h: data['bitcoin-cash'].usd_24h_change || 0 };
  },
  // 2) Coinbase spot price (no 24h change — keep last known)
  async () => {
    const res = await fetch(proxied('https://api.coinbase.com/v2/prices/BCH-USD/spot'), { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const data = await res.json();
    const p = parseFloat(data?.data?.amount);
    if (!isFinite(p) || p <= 0) return null;
    return { price: p, change24h: _cachedChange24h };
  },
  // 3) Kraken ticker (BCHUSD) — last trade price + open for 24h change
  async () => {
    const res = await fetch(proxied('https://api.kraken.com/0/public/Ticker?pair=BCHUSD'), { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const data = await res.json();
    const result = data?.result;
    const key = result && Object.keys(result)[0];
    const t = key ? result[key] : null;
    const last = t ? parseFloat(t.c?.[0]) : NaN;
    const open = t ? parseFloat(t.o) : NaN;
    if (!isFinite(last) || last <= 0) return null;
    const change24h = isFinite(open) && open > 0 ? ((last - open) / open) * 100 : _cachedChange24h;
    return { price: last, change24h };
  },
];

async function _fetchPriceOnce(): Promise<void> {
  for (const source of PRICE_SOURCES) {
    try {
      const result = await source();
      if (result) {
        _cachedPrice = result.price;
        _cachedChange24h = result.change24h;
        persistPrice(result.price, result.change24h);
        return; // first success wins
      }
    } catch {
      // try the next source
    }
  }
  // All sources failed — keep the last known (possibly persisted) price.
  console.warn('BCH price fetch failed on all sources; keeping last known price:', _cachedPrice);
}

/** Start the global price fetcher. Safe to call multiple times. */
export function initBCHPrice(): void {
  if (_priceFetchPromise) return;
  _priceFetchPromise = _fetchPriceOnce();
  if (!_priceInterval) {
    _priceInterval = setInterval(() => { _fetchPriceOnce(); }, 60000);
  }
}

/** Get the current cached BCH price (null if not yet fetched). */
export function getBCHPrice(): number | null {
  return _cachedPrice;
}

/** Wait for the first price fetch to complete, then return it. */
export async function awaitBCHPrice(): Promise<number | null> {
  if (_cachedPrice !== null) return _cachedPrice;
  if (_priceFetchPromise) await _priceFetchPromise;
  return _cachedPrice;
}

// ── React hook (unchanged API) ────────────────────────────────────
export function useBCHPrice() {
  const [price, setPrice] = useState<number | null>(_cachedPrice);
  const [change24h, setChange24h] = useState<number>(_cachedChange24h);
  const [loading, setLoading] = useState(_cachedPrice === null);

  useEffect(() => {
    // Ensure global fetcher is running
    initBCHPrice();

    let cancelled = false;

    // If we already have a cached price, use it immediately
    if (_cachedPrice !== null) {
      setPrice(_cachedPrice);
      setChange24h(_cachedChange24h);
      setLoading(false);
    }

    // Wait for first fetch then sync
    (_priceFetchPromise || Promise.resolve()).then(() => {
      if (!cancelled) {
        setPrice(_cachedPrice);
        setChange24h(_cachedChange24h);
        setLoading(false);
      }
    });

    // Periodic sync with global cache
    const sync = setInterval(() => {
      if (!cancelled && _cachedPrice !== null) {
        setPrice(_cachedPrice);
        setChange24h(_cachedChange24h);
      }
    }, 60000);

    return () => { cancelled = true; clearInterval(sync); };
  }, []);

  return { price, change24h, loading };
}

